import {GeocentricCoordinate} from '../projections';
import {
  AerialAreaMeasurement,
  AerialLengthMeasurement,
  AerialMeasurement,
  HeightMeasurement,
  Measurement,
  Measurement3D,
  MeasurementArea,
  MeasurementController,
  MeasurementLine,
  MeasurementPoint,
  MeasurementVolume,
  StationMeasurement
} from '.';
import {Vector3} from "three";
import { MeasureType } from '../../../types/measurements';

export class MeasurementGroup {
  public name;
  public controller: MeasurementController;
  public measurements = [];

  get viewer() {
    return this.controller.viewer;
  }

  constructor(controller) {
    this.controller = controller;
  }

  add(measurement) {
    this.measurements.push(measurement);
    this.updateMeasurements();
  }

  getByID(id) {
    return this.measurements.find(measurement => measurement.id === id);
  }

  removeByID(id) {
    this.removeFromArray(id);
    this.updateMeasurements();
  }

  removeFromArray(id) {
    this.measurements = this.measurements.filter(measurement => measurement.id !== id);
  }

  reset() {
    this.removeAll();
  }

  removeAll() {
    this.measurements.forEach(measurement => {
      measurement.clear();
    });
    this.measurements = [];
    this.updateMeasurements();
  }

  updateMeasurements() {
    this.controller.refreshMeasurementList();
  }

  loadMeasurements(result) {
    // Add 3d measurements
    let measure3D = result.measurements3D.map(measurement => {
      let newMeasurement = this.getEmptyMeasurement(measurement.type);
      newMeasurement.setController(this.controller);
      newMeasurement.setTitle(measurement.title);
      newMeasurement.setTimestamp(measurement.time_created);

      measurement.coordinates.forEach(vertex => {
        vertex = new Vector3().fromArray(vertex);
        if (vertex.length() === 0) return;

        newMeasurement.vertices.push(new GeocentricCoordinate(vertex));
      });

      if (newMeasurement instanceof MeasurementArea) {
        newMeasurement.generateSurface();
      }

      if (newMeasurement instanceof MeasurementVolume) {
        newMeasurement.loadExistingVolume(measurement.volume);
      }

      if (newMeasurement instanceof StationMeasurement) {
        newMeasurement.loadExistingStation(measurement);
      }

      return newMeasurement;
    });

    // Add 2d measurements
    let measure2D = result.measurements2D.map(measurement => {
      let newMeasurement = (measurement.type === MeasureType.Length)
        ? new AerialLengthMeasurement()
        : new AerialAreaMeasurement();

      newMeasurement.loadFromCoordinates(measurement.coordinates);
      newMeasurement.setController(this.controller);
      newMeasurement.setTitle(measurement.title);
      newMeasurement.setTimestamp(measurement.time_created);

      return newMeasurement;
    });

    this.measurements = [...measure3D, ...measure2D];

    // Sort by reverse timestamp ordering
    this.measurements = this.measurements.sort((a, b) =>
      a.timestamp < b.timestamp ? 1 : b.timestamp < a.timestamp ? -1 : 0
    );
    this.measurements.reverse();

    // Display the measurements
    this.measurements.forEach(measurement => {
      this.controller.renderMeasurement(measurement);
    });

    // Make sure drag object is false
    this.controller.resetDragObject();
  }

  getEmptyMeasurement(type: MeasureType) {
    if (type === MeasureType.Point) {
      return new MeasurementPoint();
    } else if (type === MeasureType.Area) {
      return new MeasurementArea();
    } else if (type === MeasureType.Volume) {
      return new MeasurementVolume();
    } else if (type === MeasureType.Height) {
      return new HeightMeasurement();
    } else if (type === MeasureType.Station) {
      return new StationMeasurement();
    }

    return new MeasurementLine();
  }

  getLineCoordinates(projection) {
    let lines = [];
    let names = [];
    let measurements = this.measurements;

    measurements.forEach(measurement => {
      if (measurement instanceof MeasurementPoint) {
        return;
      }

      let isAerial = measurement instanceof AerialMeasurement;
      let isLine = measurement instanceof Measurement3D;
      if (isAerial || isLine) {
        let line = measurement.getProjectedVertices(projection);
        lines.push(line);
        names.push(measurement.title);
      }
    });

    return {
      data: lines,
      names: names
    };
  }

  getPointCoordinates(projection) {
    let points = [];
    let names = [];
    let measurements = this.measurements;

    measurements.forEach(measurement => {
      if (measurement instanceof MeasurementPoint) {
        let point = measurement.getProjectedVertices(projection);
        points.push(point[0]);
        names.push(measurement.title);
      }
    });

    return {
      data: points,
      names: names
    };
  }

  getAreaGeometry(temporaryMeasurement: Measurement = null) {
    let measurements = [...this.measurements];

    if (temporaryMeasurement) {
      measurements.push(temporaryMeasurement);
    }

    let areaMeasurements = measurements.filter(measurement => {
      return measurement instanceof MeasurementArea;
    });

    let measureGeometry = [];
    areaMeasurements.forEach(measurement => {
      if (!measurement.valid) return;

      let volumeStatus = 0;
      if (measurement instanceof MeasurementVolume) {
        volumeStatus = measurement.calculated ? 2 : 1;
      }

      measureGeometry.push({
        vertices: measurement.surfaceVertices,
        indices: measurement.surfaceIndices,
        volumeStatus: volumeStatus
      });
    });

    measureGeometry.sort((a, b) => {
      return a.volumeStatus - b.volumeStatus;
    });
    measureGeometry.reverse();

    return measureGeometry;
  }
}
